import { useState } from 'react'
import { Filter, Download, ScrollText, AlertCircle } from 'lucide-react'
import { useAuthedFetch } from '../../hooks/useAuthedFetch'
import { useTimedOut } from '../../hooks/useTimedOut'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'

interface AuditEntry {
  event_id: string
  timestamp: string
  user_id: string
  action_type: string
  resource_id: string
  details?: Record<string, unknown> | string | null
}

export default function AuditReports() {
  const [filters, setFilters] = useState({
    action_type: '',
    from: '',
    to: '',
    limit: 50,
  })

  // Build the /audit query string from the filters. Uses the direct-fetch path
  // (see hooks/useAuthedFetch) because the axios read hangs inside /admin.
  // BUG-148: the backend compares ISO strings lexicographically, so a bare
  // `to` date (e.g. 2026-07-05) is < every timestamp on that day and excludes
  // the entire end date. Extend it to end-of-day so the range is inclusive.
  const auditPath = (() => {
    const p = new URLSearchParams()
    if (filters.action_type) p.set('action_type', filters.action_type)
    if (filters.from) p.set('from', filters.from)
    if (filters.to) p.set('to', `${filters.to}T23:59:59.999Z`)
    p.set('limit', String(filters.limit))
    return `/audit?${p.toString()}`
  })()

  // Lambda returns { events: [...], count, filters }
  const { data, isLoading, isError, refetch } = useAuthedFetch<{ events: AuditEntry[]; count: number }>(auditPath)

  // BUG-118: the Export button had no onClick at all. Build a CSV client-side
  // from the already-fetched events — no extra backend round trip needed.
  const handleExport = () => {
    const events = data?.events ?? []
    const esc = (v: unknown) => {
      const s = v == null ? '' : String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const header = ['timestamp', 'user_id', 'action_type', 'resource_id', 'details']
    const rows = events.map((e) => [
      e.timestamp,
      e.user_id,
      e.action_type,
      e.resource_id,
      e.details && typeof e.details === 'object'
        ? Object.entries(e.details).map(([k, v]) => `${k}: ${v}`).join('; ')
        : String(e.details || ''),
    ].map(esc).join(','))
    const csv = [header.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const hasEvents = !!data?.events?.length
  // Bound the skeleton so a slow/stuck read never walls the table forever.
  const loadTimedOut = useTimedOut(isLoading)
  const showSkeleton = isLoading && !loadTimedOut

  return (
    <div>
      <PageHeader
        title="Audit Log"
        description="The tamper-evident record of security-relevant actions across the platform."
        helpKey="admin-audit"
        primaryAction={
          <button
            onClick={handleExport}
            disabled={isLoading || !hasEvents}
            title={!isLoading && !hasEvents ? 'No events to export' : undefined}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 border border-sky-200 rounded-lg hover:bg-white/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={14} /> Export
          </button>
        }
      />

      {/* Filters */}
      <div className="light-card p-4 mb-4 flex flex-wrap items-center gap-4">
        <Filter size={16} className="text-gray-500" />
        <select
          value={filters.action_type}
          onChange={(e) => setFilters((prev) => ({ ...prev, action_type: e.target.value }))}
          className="px-3 py-1.5 text-sm text-gray-800 border border-sky-200 rounded-lg focus:ring-1 focus:ring-iflow-500 outline-none bg-white"
        >
          <option value="">All actions</option>
          <option value="project.create">Project Create</option>
          <option value="document.upload">Upload</option>
          <option value="document.download">Download</option>
          <option value="permission.grant">Permission Grant</option>
          <option value="permission.revoke">Permission Revoke</option>
          <option value="document.delete">Delete</option>
          <option value="review.create">Review Create</option>
        </select>
        <input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
          className="px-3 py-1.5 text-sm text-gray-800 border border-sky-200 rounded-lg focus:ring-1 focus:ring-iflow-500 outline-none bg-white"
        />
        <span className="text-sm text-gray-500">to</span>
        <input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
          className="px-3 py-1.5 text-sm text-gray-800 border border-sky-200 rounded-lg focus:ring-1 focus:ring-iflow-500 outline-none bg-white"
        />
      </div>

      {isError && !showSkeleton && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-red-500/10 border border-red-200 px-4 py-3 text-sm text-red-700">
          <span className="flex items-center gap-2">
            <AlertCircle size={16} className="flex-shrink-0" />
            Couldn't load the audit log.
          </span>
          <button onClick={refetch} className="font-medium text-red-700 hover:text-red-800 underline">Retry</button>
        </div>
      )}

      {showSkeleton ? (
        <LoadingSkeleton rows={8} type="table" />
      ) : !hasEvents ? (
        <EmptyState
          icon={ScrollText}
          title="No audit events"
          description="No actions match these filters. Widen the date range or clear the action filter to see more."
        />
      ) : (
        <div className="light-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-sky-500/5 border-b border-sky-200/50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Resource</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sky-200/30">
              {data?.events?.map((entry) => (
                <tr key={entry.event_id} className="hover:bg-white/30">
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(entry.timestamp).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800">{entry.user_id}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-sky-500/10 text-gray-700 rounded-full">
                      {entry.action_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs truncate max-w-[200px]">
                    {entry.resource_id}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {entry.details && typeof entry.details === 'object'
                      ? Object.entries(entry.details).map(([k, v]) => `${k}: ${v}`).join(', ')
                      : String(entry.details || '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {data && data.count > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-sky-200/50">
              <span className="text-sm text-gray-500">{data.count} entries shown</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
